import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Package,
  Gem,
  FolderOpen,
  Clock,
  BarChart3,
  Settings,
  HelpCircle,
  ChevronLeft,
  Plus
} from 'lucide-react'
import { cn } from '@/utils'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { useCurrentTemplate } from '@/hooks/useCurrentTemplate'
import { useAuth } from '@/hooks/useAuth'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

interface NavItem {
  label: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  badge?: string | number
  children?: NavItem[]
}

const navigationItems: NavItem[] = [
  {
    label: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard
  },
  {
    label: 'Materials',
    href: '/materials',
    icon: Gem,
    children: [
      { label: 'View All Materials', href: '/materials', icon: Gem },
      { label: 'Add Material/Purchase', href: '/materials/add', icon: Plus },
      { label: 'Stock Adjustment', href: '/materials/adjustment', icon: Package }
    ]
  },
  {
    label: 'Projects',
    href: '/projects',
    icon: FolderOpen,
    children: [
      { label: 'View Projects', href: '/projects', icon: FolderOpen },
      { label: 'Create Project', href: '/projects/create', icon: Plus }
    ]
  },
  {
    label: 'Products',
    href: '/products',
    icon: Package,
    children: [
      { label: 'View Products', href: '/products', icon: Package }
    ]
  },
  {
    label: 'Time Tracking',
    href: '/time-tracking',
    icon: Clock
  },
  {
    label: 'Reports',
    href: '/reports',
    icon: BarChart3,
    children: [
      { label: 'View Reports', href: '/reports', icon: BarChart3 },
      { label: 'Stock Adjustments', href: '/reports/stock-adjustments', icon: Package }
    ]
  }
]

const bottomItems: NavItem[] = [
  {
    label: 'Settings',
    href: '/settings',
    icon: Settings
  },
  {
    label: 'Help & Support',
    href: '/help',
    icon: HelpCircle
  }
]

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation()
  const { template } = useCurrentTemplate()
  const { user } = useAuth()

  const isActiveRoute = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === href
    }
    // More precise matching to avoid conflicts
    if (href === '/materials') {
      return location.pathname === href || location.pathname.startsWith('/materials/')
    }
    if (href === '/projects') {
      return location.pathname === href || location.pathname.startsWith('/projects/')
    }
    if (href === '/products') {
      return location.pathname === href || location.pathname.startsWith('/products/')
    }
    return location.pathname.startsWith(href)
  }

  const renderNavItem = (item: NavItem, level = 0) => {
    const isActive = isActiveRoute(item.href)
    const hasChildren = item.children && item.children.length > 0
    const hasActiveChild = hasChildren && item.children?.some(child => isActiveRoute(child.href))
    const shouldShowChildren = hasChildren // Always show children if they exist
    const shouldHighlight = isActive || hasActiveChild

    return (
      <div key={`${item.href}-${level}`}>
        <Link
          to={item.href}
          onClick={() => {
            if (window.innerWidth < 1024) {
              onClose()
            }
          }}
          className={cn(
            'flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors',
            level > 0 && 'ml-4 pl-8',
            shouldHighlight
              ? 'bg-blue-600 text-white'
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
          )}
        >
          <div className="flex items-center space-x-3">
            <item.icon className="h-5 w-5 flex-shrink-0" />
            <span>{item.label}</span>
          </div>
          {item.badge && (
            <Badge
              variant={shouldHighlight ? 'default' : 'secondary'}
              size="sm"
            >
              {item.badge}
            </Badge>
          )}
        </Link>

        {/* Always show children if they exist */}
        {shouldShowChildren && (
          <div className="mt-1 space-y-1">
            {item.children!.map((child, index) => renderNavItem(child, level + 1))}
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'w-64 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 flex flex-col h-full',
          // Desktop: always visible
          'hidden lg:flex',
          // Mobile: show when open
          isOpen && 'flex fixed inset-y-0 left-0 z-50'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700">
          <Link to="/dashboard" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-jewelry-primary to-jewelry-secondary rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">
                {template?.customBusinessName
                  ? template.customBusinessName.split(' ').map(word => word[0]).join('').substring(0, 2).toUpperCase()
                  : template?.name
                    ? template.name.split(' ').map(word => word[0]).join('').substring(0, 2).toUpperCase()
                    : 'BM'
                }
              </span>
            </div>
            <span className="font-semibold text-gray-900 dark:text-white">
              {template?.customBusinessName || 'My Business'}
            </span>
          </Link>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="lg:hidden"
            aria-label="Close sidebar"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
          {navigationItems.map(item => renderNavItem(item))}
        </nav>

        {/* Bottom section */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-4 space-y-2">
          {bottomItems.map(item => renderNavItem(item))}
        </div>
      </aside>
    </>
  )
}